import React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { baseUnitState, measurementsState, productMeasurements } from '@state';
import { useMount, useUpdateEffect } from 'ahooks';
import { getMeasurementInUnit, roundHalf, findClosestIndex } from '@utils';
import Select from 'react-select';
import { loadStateFromURL, stateKeys } from '../loadableStateUtils';
import { Popover, PopoverTrigger, PopoverContent } from '../../parts';

let lookupHeightTable = [2, 2.5, 3, 3.5, 4, 4.5, 5];
let lookupHeightTableValues = [2, 3, 4, 5, 6, 7, 8];

let productSpecificMaxHeight = {
  yoema: 2,
};

// const findClosestMeasurement = (valuesList, value) =>
//   valuesList.reduce((prev, curr) => {
//     return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
//   });

const selectStyles = {
  control: (provided, state) => {
    return {
      ...provided,
      borderRadius: '1.8em',
      minHeight: 28,
      paddingLeft: '0.9em',
      paddingRight: '0.4em',
      backgroundColor: 'hsla(300, 100%, 100%, 1.000)',
      boxShadow: state.isFocused ? '0 0 0 1px hsla(14, 41%, 88%, 1.000)' : '0 0 0 1px hsla(109, 0%, 80%, 1.000)',
      border: '1px',
      borderColor: state.isFocused ? 'hsla(14, 41%, 88%, 1.000)' : 'hsla(204, 3%, 31%, 1.000)',
    };
  },
  menu: (provided, state) => {
    return {
      ...provided,
      backgroundColor: 'hsla(300, 100%, 100%, 1.000)',
    };
  },
  option: (provided, state) => {
    return {
      ...provided,
      fontFamily: 'var(--sans)',
      backgroundColor: state.isSelected ? 'hsla(204, 3%, 31%, 1.000)' : state.isFocused ? 'var(--color-ui-muted)' : 'transparent',
      color: state.isSelected ? 'white' : 'hsla(180, 33%, 1%, 1.000)',
    };
  },
  input: (provided, state) => {
    return {
      ...provided,
    };
  },
};

// const ConditionalLimit = ({ specs }) => {
//   const baseUnit = useRecoilValue(baseUnitState);
//   const getMaxH = () => {
//     let maxH = productSpecificMaxHeight[specs.name] || 8;
//     let lookupValue = Math.min(specs.width.max * specs.smallest_compartment.w, specs.length.max * specs.smallest_compartment.l);
//     let maxHeight = roundHalf(Math.min(lookupHeightTableValues[findClosestIndex(lookupHeightTable, roundHalf(lookupValue))], maxH));
//     return getMeasurementInUnit(maxHeight, 'imperial', baseUnit);
//   };

//   let maxH = getMaxH();
//   if (specs.height.max == maxH) {
//     return null;
//   }
//   return (
//     <div>
//       {baseUnit === 'imperial' && <small>Height up to {maxH}" based on configuration.</small>}
//       {baseUnit === 'metric' && <small>Height up to {Math.floor(maxH)}" based on configuration.</small>}
//     </div>
//   );
// };

const SizeInput = ({ dimension, specs }) => {
  const allMeasurements = useRecoilValue(productMeasurements);
  const [measurement, setMeasurement] = useRecoilState(measurementsState(dimension));
  const baseUnit = useRecoilValue(baseUnitState);
  const productType = specs?.product_type?.name;

  useMount(() => {
    if (window.location.href.includes(stateKeys[dimension])) {
      loadStateFromURL({ stateKey: dimension, setter: setMeasurement, defaultValue: min, value: measurement });
    } else {
      if (!measurement) {
        // setMeasurement(min);
      }
    }
  });

  let sizeRange = {
    metric: {
      min: Number(specs.sizes[0][dimension]['min']) * 25.4,
      max: Number(specs.sizes[0][dimension]['max']) * 25.4,
      roundTo: Number(0),
    },
    imperial: {
      min: Number(specs.sizes[0][dimension]['min']),
      max: Number(specs.sizes[0][dimension]['max']),
      roundTo: Number(2),
    },
  };

  var min = Number(sizeRange[baseUnit]['min'].toFixed(sizeRange[baseUnit]['roundTo']));
  var max = Number(sizeRange[baseUnit]['max'].toFixed(sizeRange[baseUnit]['roundTo']));

  const getFlexibleMaxValue = (currentMax) => {
    let maxH = specs.sizes[0].height.max ? specs.sizes[0].height.max : productSpecificMaxHeight[specs.name] || 8;
    let lookupValue = Math.min(
      getMeasurementInUnit(allMeasurements.width || 0, baseUnit, 'imperial') * Number(specs.sizes[0].smallest_compartment.w),
      getMeasurementInUnit(allMeasurements.length || 0, baseUnit, 'imperial') * Number(specs.sizes[0].smallest_compartment.l)
    );
    let maxHeight = roundHalf(Math.min(lookupHeightTableValues[findClosestIndex(lookupHeightTable, roundHalf(lookupValue))] || 0, maxH));
    let maxNew = getMeasurementInUnit(maxHeight, 'imperial', baseUnit);
    if (Boolean(maxNew)) {
      return Number(getMeasurementInUnit(maxHeight, 'imperial', baseUnit)).toFixed(sizeRange[baseUnit]['roundTo']);
    } else {
      return currentMax;
    }
  };

  const handleMeasurementsInput = (measurement) => (data) => {
    setMeasurement(data.value);
  };

  /**
   * Drawer Height takes into consideration the overall size so it won't create
   * drawers that are too deep for their external dimensions
   */
  if (dimension === 'height' && productType === 'Organizers') {
    max = getFlexibleMaxValue(max);
    if (max < allMeasurements.height) {
      handleMeasurementsInput('height')({ value: Number(max), label: String(max) });
    }
  }

  useUpdateEffect(() => {
    switch (baseUnit) {
      case 'metric':
        setMeasurement(Math.round(Number(measurement * 25.4)));
        break;

      case 'imperial':
        setMeasurement(Number(measurement / 25.4));
        break;
    }
  }, [baseUnit]);

  const options = () => {
    let increment = baseUnit === 'imperial' ? 0.5 : 1;

    let results = [];
    for (let index = min; index <= max; index += increment) {
      results.push({ value: index, label: `${index}"` });
    }
    return results;
  };

  let referenceValue = baseUnit === 'metric' ? Number(measurement?.toFixed(0)) : roundHalf(measurement);
  let currentValue = options().find((item) => item.value === referenceValue);

  if (min == max) {
    setMeasurement(Number(min));
  }

  return (
    <div className={`sizeInput ${dimension}`}>
      <div className="sizeInput__label">
        {dimension}
        {dimension === 'height' && ' *'}
        {dimension === 'height' && (
          <Popover>
            <PopoverTrigger asChild>
              <span className="helper">help</span>
            </PopoverTrigger>
            <PopoverContent>
              <p>Adjust length and width first. If your desired height is not available, please try a template with larger compartments.</p>
            </PopoverContent>
          </Popover>
        )}
      </div>
      {min === max ? (
        <div className="sizeInput__fixedMetric">
          {min} {baseUnit === 'imperial' ? 'inches' : 'mm'}
        </div>
      ) : (
        <>
          <label htmlFor={`sizeSelector-for-${dimension}`} className="screen-reader-text">
            {dimension}
          </label>
          <Select
            className="sizeSelector"
            inputId={`sizeSelector-for-${dimension}`}
            styles={selectStyles}
            value={currentValue}
            options={options()}
            onChange={handleMeasurementsInput(dimension)}
            aria-required="true"
          />
          {/* <small>
            Min: {min} / Max: {max}
          </small>
          {productType === 'Organizers' && dimension === 'height' && <ConditionalLimit specs={specs}></ConditionalLimit>} */}
        </>
      )}
    </div>
  );
};

export default SizeInput;
